import { type ReactNode } from 'react';
import { createSafeContext } from '@mews-framework/utils';
import { addMetrics } from '../openTelemetry';

export interface JourneyTracker {
    start: (metricName?: string) => void;
    stop: () => void;
    cancel: () => void;
    getTotalDuration: () => number;
    isActive: () => boolean;
    trackAsync: <T>(promise: Promise<T> | T, label?: string) => Promise<T>;
    trackAsyncAndStop: <T>(promise: Promise<T> | T, label?: string) => Promise<T>;
}

/**
 * Creates a journey tracker that measures React component-level work time.
 * Uses React Profiler to track component renders and updates.
 */
export const createJourneyTracker = (): JourneyTracker => {
    let isTracking = false;
    let metricName: string | undefined;
    let hasFinalAction = false;
    const trackedPeriods: Array<{ start: number; end: number }> = [];

    /**
     * Merges overlapping time periods and calculates the total unique duration.
     * This ensures parallel renders don't get double-counted.
     *
     * Example: If 3 components render in parallel for 10ms each,
     * instead of counting 30ms, we count 10ms (what user actually perceives).
     */
    const mergeOverlappingPeriods = (periods: Array<{ start: number; end: number }>): number => {
        if (periods.length === 0) {
            return 0;
        }

        const sorted = [...periods].sort((a, b) => a.start - b.start);
        const merged: Array<{ start: number; end: number }> = [sorted[0]];

        for (let i = 1; i < sorted.length; i++) {
            const current = sorted[i];
            const lastMerged = merged[merged.length - 1];

            if (current.start <= lastMerged.end) {
                lastMerged.end = Math.max(lastMerged.end, current.end);
            } else {
                merged.push(current);
            }
        }

        return merged.reduce((total, period) => total + (period.end - period.start), 0);
    };

    const start = (name?: string) => {
        if (isTracking) {
            return;
        }

        isTracking = true;
        metricName = name;
        hasFinalAction = false;
        trackedPeriods.length = 0;
    };

    const stop = () => {
        if (!isTracking) {
            return;
        }

        isTracking = false;
        hasFinalAction = true;

        if (metricName) {
            const totalDuration = getTotalDuration();
            addMetrics({ [metricName]: totalDuration });
        }
    };

    const cancel = () => {
        if (!isTracking || hasFinalAction) {
            return;
        }

        isTracking = false;
    };

    const getTotalDuration = (): number => {
        const mergedDuration = mergeOverlappingPeriods(trackedPeriods);
        return Math.ceil(mergedDuration);
    };

    /**
     * Tracks async work (like dispatch calls that return promises)
     *
     * Note: This measures from when we start tracking to when the promise resolves.
     * If the promise was created earlier, we might miss some execution time, but this
     * is the best we can do without modifying the promise creation itself.
     *
     * If the provided value is not a promise, it will be returned as-is without tracking.
     */
    const trackAsync = async <T,>(promise: Promise<T> | T, _label?: string): Promise<T> => {
        if (!isTracking) {
            return promise as Promise<T>;
        }

        if (!promise || typeof (promise as $TSFixMe)?.then !== 'function') {
            return promise as Promise<T>;
        }

        const dispatchTrackingStartTime = performance.now();

        try {
            const result = await promise;

            if (!isTracking) {
                return result;
            }

            const trackingEndTime = performance.now();
            const period = {
                start: dispatchTrackingStartTime,
                end: trackingEndTime,
            };
            trackedPeriods.push(period);

            return result;
        } catch (error) {
            if (!isTracking) {
                throw error;
            }

            const trackingEndTime = performance.now();
            const period = {
                start: dispatchTrackingStartTime,
                end: trackingEndTime,
            };
            trackedPeriods.push(period);

            throw error;
        }
    };

    const isActive = (): boolean => {
        return isTracking;
    };

    /**
     * Tracks async work and stops tracking when the promise resolves or rejects.
     * This is useful for the final action in a journey (e.g., booking a reservation).
     *
     * If the provided value is not a promise, tracking will be stopped immediately.
     */
    const trackAsyncAndStop = async <T,>(promise: Promise<T> | T, _label?: string): Promise<T> => {
        hasFinalAction = true;

        try {
            const result = await trackAsync(promise, _label);
            stop();
            return result;
        } catch (error) {
            stop();
            throw error;
        }
    };

    return {
        start,
        stop,
        cancel,
        getTotalDuration,
        isActive,
        trackAsync,
        trackAsyncAndStop,
    };
};

/**
 * React Context for the journey tracker instance
 */
export const {
    JourneyTrackerContextProvider,
    useJourneyTrackerContext: useJourneyTracker,
    rawContext: JourneyTrackerContext,
} = createSafeContext<'JourneyTrackerContext', JourneyTracker>('JourneyTrackerContext');

/**
 * Module-level ref to the current tracker instance.
 * This is set by JourneyTrackerProvider and allows non-React code (like Redux middleware)
 * to access the tracker instance. The Provider remains the source of truth.
 */
let currentTrackerRef: JourneyTracker | null = null;

/**
 * Gets the current tracker instance from the module-level ref.
 * This is primarily for use by Redux middleware and other non-React code.
 * React components should use useJourneyTracker hook instead.
 *
 * @returns The current tracker instance, or null if no provider is mounted
 */
export const getCurrentJourneyTracker = (): JourneyTracker => {
    if (currentTrackerRef === null) {
        currentTrackerRef = createJourneyTracker();
    }

    return currentTrackerRef;
};

/**
 * Provider component that supplies the singleton journey tracker instance to the React tree.
 * Reuses the same tracker instance for all providers.
 */
export const JourneyTrackerProvider = ({ children }: { children: ReactNode }) => {
    const tracker = getCurrentJourneyTracker();

    return <JourneyTrackerContextProvider value={tracker}>{children}</JourneyTrackerContextProvider>;
};
